import { useCallback, useEffect, useState } from "react";
import PulseScreen from "./screens/Pulse";
import FlowsScreen from "./screens/Flows";
import ExploreScreen from "./screens/Explore";
import McpScreen from "./screens/Mcp";
import BringInScreen from "./screens/BringIn";
import SitesScreen from "./screens/Sites";
import BackupsScreen from "./screens/Backups";
import DataMapScreen from "./screens/DataMap";

type User = { user_id: number; email: string; name: string; role: string };

type Status = "loading" | "signed-out" | "signed-in";

const NAV_TOP = ["Pulse", "Flows", "Explore", "MCP", "Bring in data"] as const;
const NAV_SYSTEM = ["Data map", "Sites", "Backups"] as const;

export default function App() {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [active, setActive] = useState<string>("Pulse");

  const refreshStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/status");
      if (res.status === 401) {
        setStatus("signed-out");
        setUser(null);
        return;
      }
      const data = (await res.json()) as { user: User | null };
      if (data.user) {
        setUser(data.user);
        setStatus("signed-in");
      } else {
        setStatus("signed-out");
        setUser(null);
      }
    } catch {
      setStatus("signed-out");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  if (status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-neutral-400">
        Loading&hellip;
      </div>
    );
  }

  if (status === "signed-out" || !user) {
    return <Login onSignedIn={refreshStatus} />;
  }

  return <Shell user={user} active={active} onNavigate={setActive} onSignedOut={refreshStatus} />;
}

function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setError(data.error ?? "Invalid username or password");
        return;
      }
      onSignedIn();
    } catch {
      setError("Could not reach Core Data — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-4">
      <div className="w-full max-w-sm rounded-card border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
        <h1 className="text-2xl text-ink">
          Core<span className="text-brand">Data</span>
        </h1>
        <p className="mt-1 text-sm text-neutral-500">Sign in to continue.</p>
        <form className="mt-6 flex flex-col gap-4" onSubmit={submit}>
          <label className="flex flex-col gap-1 text-sm font-medium text-neutral-700">
            Username
            <input
              className="rounded-lg border border-neutral-300 px-3 py-2.5 text-base outline-none focus:border-brand"
              type="text"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-neutral-700">
            Password
            <input
              className="rounded-lg border border-neutral-300 px-3 py-2.5 text-base outline-none focus:border-brand"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          {error && <p className="text-sm text-brand">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="min-h-[44px] rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Shell({
  user,
  active,
  onNavigate,
  onSignedOut,
}: {
  user: User;
  active: string;
  onNavigate: (name: string) => void;
  onSignedOut: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  async function signOut() {
    await fetch("/api/auth/logout", { method: "POST" });
    onSignedOut();
  }

  function navigate(name: string) {
    onNavigate(name);
    setMenuOpen(false);
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-paper lg:flex-row">
      {/* Mobile top bar */}
      <header
        className="flex shrink-0 items-center justify-between border-b border-neutral-200 bg-white px-4 py-2.5 lg:hidden"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="text-xl text-ink">
          Core<span className="text-brand">Data</span>
        </div>
        <button
          onClick={() => setMenuOpen(true)}
          aria-label="Menu"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-neutral-600 hover:bg-neutral-50"
        >
          <MenuIcon />
        </button>
      </header>

      {/* Desktop left rail */}
      <nav className="hidden w-56 shrink-0 flex-col gap-0.5 border-r border-neutral-200 bg-white p-3.5 lg:flex">
        <div className="px-2.5 pb-4 pt-1 text-xl text-ink">
          Core<span className="text-brand">Data</span>
        </div>
        {NAV_TOP.map((name) => (
          <RailItem key={name} name={name} active={active === name} onClick={() => onNavigate(name)} />
        ))}
        <div className="px-2.5 pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-wider text-neutral-400">
          Datasets
        </div>
        <div className="mt-auto flex flex-col gap-0.5">
          <div className="px-2.5 pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-wider text-neutral-400">
            System
          </div>
          {NAV_SYSTEM.map((name) => (
            <RailItem key={name} name={name} active={active === name} onClick={() => onNavigate(name)} />
          ))}
          <button
            onClick={signOut}
            className="mt-2 flex items-center justify-between rounded-lg px-3 py-2 text-left text-xs text-neutral-500 hover:bg-neutral-50"
          >
            {user.name} · {user.role}
          </button>
        </div>
      </nav>

      <main className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden p-4 pb-24 lg:p-7 lg:pb-7">
        <Screen name={active} />
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-20 flex border-t border-neutral-200 bg-white lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {NAV_TOP.map((name) => (
          <TabItem key={name} name={name} active={active === name} onClick={() => onNavigate(name)} />
        ))}
      </nav>

      {/* Mobile slide-over sheet: Datasets / System / sign out */}
      {menuOpen && (
        <div className="fixed inset-0 z-30 lg:hidden" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="absolute right-0 top-0 flex h-full w-72 max-w-[85vw] flex-col gap-0.5 bg-white p-4 shadow-xl"
            style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <div className="text-lg text-ink">Menu</div>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="flex h-11 w-11 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-50"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="px-2.5 pb-1.5 pt-3 text-[11px] font-bold uppercase tracking-wider text-neutral-400">
              Datasets
            </div>
            <div className="px-2.5 pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-wider text-neutral-400">
              System
            </div>
            {NAV_SYSTEM.map((name) => (
              <RailItem key={name} name={name} active={active === name} onClick={() => navigate(name)} />
            ))}
            <button
              onClick={signOut}
              className="mt-auto flex min-h-[44px] items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-neutral-500 hover:bg-neutral-50"
            >
              {user.name} · {user.role}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Screen({ name }: { name: string }) {
  switch (name) {
    case "Pulse":
      return <PulseScreen />;
    case "Flows":
      return <FlowsScreen />;
    case "Explore":
      return <ExploreScreen />;
    case "MCP":
      return <McpScreen />;
    case "Bring in data":
      return <BringInScreen />;
    case "Data map":
      return <DataMapScreen />;
    case "Sites":
      return <SitesScreen />;
    case "Backups":
      return <BackupsScreen />;
    default:
      return (
        <>
          <h1 className="text-2xl text-ink">{name}</h1>
          <p className="mt-2 text-sm text-neutral-500">Coming soon.</p>
        </>
      );
  }
}

function RailItem({ name, active, onClick }: { name: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        "flex min-h-[44px] items-center justify-between gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium " +
        (active ? "bg-red-50 text-brand" : "text-neutral-600 hover:bg-neutral-50")
      }
    >
      {name}
    </button>
  );
}

function TabItem({ name, active, onClick }: { name: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex min-h-[44px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium ${
        active ? "text-brand" : "text-neutral-500"
      }`}
    >
      <TabIcon name={name} />
      <span>{name === "Bring in data" ? "Bring in" : name}</span>
    </button>
  );
}

function TabIcon({ name }: { name: string }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "Pulse":
      return (
        <svg {...common}>
          <path d="M3 12h4l2 7 4-14 2 7h6" />
        </svg>
      );
    case "Flows":
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="2.5" />
          <circle cx="6" cy="18" r="2.5" />
          <circle cx="18" cy="12" r="2.5" />
          <path d="M8.2 6.9 15.8 11M8.2 17.1 15.8 13" />
        </svg>
      );
    case "Explore":
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case "MCP":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="7" rx="1.5" />
          <rect x="3" y="13" width="18" height="7" rx="1.5" />
          <path d="M7 7.5h.01M7 16.5h.01" />
        </svg>
      );
    case "Bring in data":
      return (
        <svg {...common}>
          <path d="M12 3v12m0 0-4-4m4 4 4-4" />
          <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
      );
    default:
      return null;
  }
}

function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
